import { validate, type FieldErrors, type FormState } from '../lib/validation';
import type { SettlementRail } from '../lib/types';

interface SettlementFormProps {
  form: FormState;
  onChange: (patch: Partial<FormState>) => void;
  onSubmit: () => void;
  onClear: () => void;
  submitting: boolean;
  rails: SettlementRail[];
  railsLoading: boolean;
  railsUnavailable: boolean;
}

function fieldError(errors: FieldErrors, key: keyof FieldErrors): JSX.Element | null {
  if (!errors[key]) return null;
  return <p className="mt-1 text-xs text-red-400">{errors[key]}</p>;
}

export function SettlementForm({ form, onChange, onSubmit, onClear, submitting, rails, railsLoading, railsUnavailable }: SettlementFormProps): JSX.Element {
  const errors = validate(form);
  const valid = Object.keys(errors).length === 0;

  const inputClass =
    'mt-1 w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-kore-accent';

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !submitting) onSubmit();
      }}
    >
      <div>
        <label htmlFor="koreId" className="block text-sm text-slate-300">KoreID <span className="text-red-400">*</span></label>
        <input id="koreId" className={inputClass} value={form.koreId} onChange={(e) => onChange({ koreId: e.target.value })} aria-required="true" />
        {fieldError(errors, 'koreId')}
      </div>

      <div>
        <label htmlFor="bankRequestReference" className="block text-sm text-slate-300">Bank Request Reference <span className="text-slate-500">(optional)</span></label>
        <input id="bankRequestReference" className={inputClass} value={form.bankRequestReference} onChange={(e) => onChange({ bankRequestReference: e.target.value })} />
        <p className="mt-1 text-xs text-slate-500">Optional. If left blank, a reference is generated automatically.</p>
        {fieldError(errors, 'bankRequestReference')}
      </div>

      <div>
        <label htmlFor="bankName" className="block text-sm text-slate-300">Bank Name <span className="text-red-400">*</span></label>
        <input id="bankName" className={inputClass} value={form.bankName} onChange={(e) => onChange({ bankName: e.target.value })} aria-required="true" />
        {fieldError(errors, 'bankName')}
      </div>

      <div>
        <label htmlFor="settlementRail" className="block text-sm text-slate-300">Settlement Rail <span className="text-red-400">*</span></label>
        <select
          id="settlementRail"
          className={inputClass}
          value={form.settlementRail}
          disabled={railsLoading || railsUnavailable || submitting}
          onChange={(e) => onChange({ settlementRail: e.target.value })}
          aria-required="true"
        >
          <option value="">{railsLoading ? 'Loading live rails…' : 'Select a live rail'}</option>
          {rails.map((rail) => (
            <option key={rail.id} value={rail.id}>
              {rail.name} — LIVE{rail.corridor ? ` — ${rail.corridor}` : ''}
            </option>
          ))}
        </select>
        {railsUnavailable && <p className="mt-1 text-xs text-red-400">Rail catalogue unavailable. Settlement is blocked fail-closed.</p>}
        {fieldError(errors, 'settlementRail')}
      </div>

      <div>
        <label htmlFor="accountIdentifier" className="block text-sm text-slate-300">Account Number / Identifier <span className="text-slate-500">(optional)</span></label>
        <input id="accountIdentifier" className={inputClass} value={form.accountIdentifier} onChange={(e) => onChange({ accountIdentifier: e.target.value })} autoComplete="off" />
        <p className="mt-1 text-xs text-slate-500">Masked in logs and results. Provide only if required by account validation.</p>
      </div>

      <div>
        <label htmlFor="operatorNotes" className="block text-sm text-slate-300">Operator Notes <span className="text-red-400">*</span></label>
        <textarea id="operatorNotes" rows={3} className={inputClass} value={form.operatorNotes} onChange={(e) => onChange({ operatorNotes: e.target.value })} aria-required="true" />
        {fieldError(errors, 'operatorNotes')}
      </div>

      <div className="flex items-start gap-2">
        <input id="confirmed" type="checkbox" checked={form.confirmed} onChange={(e) => onChange({ confirmed: e.target.checked })} className="mt-1" aria-required="true" />
        <label htmlFor="confirmed" className="text-sm text-slate-300">
          I confirm this action is being performed only in response to an authorised bank request.
        </label>
      </div>
      {fieldError(errors, 'confirmed')}

      <div className="flex items-start gap-2 rounded border border-amber-700 bg-amber-900/20 p-3">
        <input id="sovereignOverride" type="checkbox" checked={form.sovereignOverride} onChange={(e) => onChange({ sovereignOverride: e.target.checked })} className="mt-1" />
        <label htmlFor="sovereignOverride" className="text-sm text-amber-200">
          Omega777 master quantum-sovereign superadmin override (audited; permits re-execution of a
          prior idempotency key). This never bypasses downstream gates or fabricates a result.
        </label>
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <button
          type="submit"
          disabled={!valid || submitting}
          className="rounded bg-kore-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Force Resettle, Resync, PACS.028 Nudge &amp; Reconcile
        </button>
        <button type="button" onClick={onClear} disabled={submitting} className="rounded border border-slate-500 bg-slate-800 px-4 py-2 text-sm hover:bg-slate-700 disabled:opacity-50">
          Clear
        </button>
      </div>
    </form>
  );
}
